import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { api, money } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Menu() {
  const [params, setParams] = useSearchParams();
  const [category, setCategory] = useState(params.get("cat") || "all");
  const [search, setSearch] = useState("");
  const [maxPrice, setMaxPrice] = useState(40);
  const [sort, setSort] = useState("default");
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const c = params.get("cat");
    if (c) setCategory(c);
  }, [params]);

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const filtered = useMemo(() => {
    let list = products.filter((p) => {
      if (category !== "all" && p.categorie_slug !== category) return false;
      if (search && !p.nom.toLowerCase().includes(search.toLowerCase()) && !(p.description || "").toLowerCase().includes(search.toLowerCase())) return false;
      if (p.prix > maxPrice) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.prix - b.prix);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.prix - a.prix);
    if (sort === "name") list = [...list].sort((a, b) => a.nom.localeCompare(b.nom));
    return list;
  }, [products, category, search, maxPrice, sort]);

  const selectCat = (id) => {
    setCategory(id);
    if (id === "all") setParams({}); else setParams({ cat: id });
  };

  const Filters = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground uppercase tracking-wider mb-3">Catégories</h3>
        <div className="flex flex-col gap-1">
          <CatChip active={category === "all"} onClick={() => selectCat("all")} label="Tous les produits" testid="filter-cat-all" />
          {categories.map((c) => (
            <CatChip key={c.id} active={category === c.id} onClick={() => selectCat(c.id)} label={c.nom} testid={`filter-cat-${c.id}`} />
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground uppercase tracking-wider mb-3">Prix maximum</h3>
        <input type="range" min={4} max={40} step={0.5} value={maxPrice} onChange={(e) => setMaxPrice(parseFloat(e.target.value))}
          data-testid="filter-price" className="w-full accent-coral" />
        <p className="text-sm text-muted-foreground mt-1">Jusqu'à <span className="text-coral font-bold">{money(maxPrice)}</span></p>
      </div>
      <div>
        <h3 className="font-heading text-sm font-semibold text-foreground uppercase tracking-wider mb-3">Trier par</h3>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort"
          className="w-full px-3 py-2.5 rounded-lg bg-foreground/5 border border-border text-foreground text-sm focus:border-coral focus:outline-none">
          <option value="default">Ordre du menu</option>
          <option value="price-asc">Prix croissant</option>
          <option value="price-desc">Prix décroissant</option>
          <option value="name">Nom (A-Z)</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-foreground">Mets préparés</h1>
        <p className="text-muted-foreground mt-2">42 produits faits maison · Entrées, plats, portions familiales et produits maison. Pour un événement, découvrez le <a href="/buffets" className="text-coral hover:underline">Studio Buffet</a>.</p>
      </div>

      <div className="relative mb-6 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="menu-search"
          placeholder="Rechercher un plat, une sauce, un pâté..."
          className="w-full pl-12 pr-4 py-3.5 rounded-full bg-ink-800 border border-border text-foreground placeholder:text-muted-foreground focus:border-coral focus:outline-none" />
      </div>

      <button onClick={() => setFiltersOpen(true)} data-testid="menu-filters-toggle"
        className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-ink-800 border border-border mb-6">
        <SlidersHorizontal className="h-4 w-4 text-coral" /> Filtres
      </button>

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        <aside className="hidden lg:block sticky top-24 self-start p-5 rounded-2xl bg-ink-800 border border-border">
          <Filters />
        </aside>

        <div>
          {isLoading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-80 rounded-2xl bg-ink-800 animate-pulse" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="menu-empty">Aucun produit ne correspond à votre recherche.</div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mb-4">{filtered.length} produit(s)</p>
              <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {filtered.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            </>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setFiltersOpen(false)} />
          <div className="relative ml-auto w-80 max-w-[85vw] h-full bg-card border-l border-border p-6 overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading font-bold text-lg">Filtres</h2>
              <button onClick={() => setFiltersOpen(false)}><X className="h-5 w-5" /></button>
            </div>
            <Filters />
            <button onClick={() => setFiltersOpen(false)} className="mt-6 w-full py-3 rounded-xl bg-primary text-primary-foreground font-heading font-semibold">Voir {filtered.length} produits</button>
          </div>
        </div>
      )}
    </div>
  );
}

function CatChip({ active, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left px-3 py-2 rounded-lg text-sm transition-colors ${active ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:bg-foreground/5"}`}>
      {label}
    </button>
  );
}
